import React, { useRef, useEffect } from 'react';

import { Helmet } from 'react-helmet';
import { navigate } from 'gatsby';
import { bool } from 'prop-types';

import { BiArrowBack } from 'react-icons/bi';
import {
  Box,
  Text,
  Center,
  Flex,
  Divider,
  useToast,
  Button,
  Spinner,
  Image,
  Card,
} from '@chakra-ui/react';

import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
} from '@chakra-ui/react';

import QRCode from 'react-qr-code';
import loadable from '@loadable/component';

import { useImaginationRoom } from '@invitato/imagination-room';
import useLang from '@/hooks/useLang';
import generateShift from '@/hooks/useShift';
import useQueryString from '@/hooks/useQueryString';
import { useGuest } from '@/context/guest';

import { THE_BRIDE, WEDDING_RESEPSI_DATE, WEDDING_RESEPSI_LOC_NAME } from '@/constants';
import LOGO_INVITATO from '@/images/invitato.png';
import IMG_QR_COVER from '@/images/qrcover.jpg';

import txt from './locales';
import QRButton from '../QRCodeButton';

// @important - please do don't change to CDN github
// we will got failed export image because CORS issue
import { ENABLE_SHIFT_TIME_RECEPTION } from '@/constants/feature-flags';

const LazyExportPNG = loadable(() =>
  import(/* webpackChunkName: "invitato-export-png" */ './ExportPNG'),
);

/**
 * Function to render QRCard version 2
 * Support:
 * - Download QR as PNG
 * - Open QR in new page
 */
function QRCardSection({ isDirectLink, ...rest }) {
  const toast = useToast();
  const { getQueryValue } = useQueryString();
  const isAutoDownload = getQueryValue('autoDownload') == '1';

  const lang = useLang();
  const ticketRef = useRef(null);
  const isShowToaster = useRef(false);
  const { isLoading, guest } = useGuest();

  const { code, name, desc, shift, guest_quota, guest_confirm, vip_text } = guest;

  useEffect(() => {
    if (
      code &&
      !isLoading &&
      window.matchMedia &&
      window.matchMedia('(prefers-color-scheme: dark)').matches &&
      !isDirectLink &&
      !isShowToaster.current &&
      !isAutoDownload
    ) {
      isShowToaster.current = true;
      toast({
        isClosable: true,
        status: 'warning',
        duration: null,
        description: (
          <Text fontSize="sm">
            <span dangerouslySetInnerHTML={{ __html: txt.darkmodeDesc[lang] }} />
          </Text>
        ),
      });
    }
  }, [code, isAutoDownload, isDirectLink, isLoading, lang, toast]);

  const validText = txt.valid[lang].replace('{{count}}', guest_confirm || guest_quota);
  const isVIP = vip_text.toLowerCase().includes('vip');

  const { isOpen, onOpen, onClose } = useDisclosure();
  const { onGetFinalContent } = useImaginationRoom();

  // IF code is not found, just ignore it
  if (!code) return null;

  return (
    <Box paddingTop={'1.5rem'}>
      {isLoading ? (
        <Center height="100px">
          <Spinner thickness="4px" speed="0.65s" emptyColor="gray.200" color="blue.500" size="xl" />
        </Center>
      ) : (
        <>
          <Helmet>
            <link
              href="https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700&display=swap"
              rel="stylesheet"
            />
          </Helmet>
          <Box height="28px" />
          {/* ================================== */}
          {/* ========== START QR BOX ========== */}
          {/* ================================== */}
          <Flex justify="center" align="center" className="QRBox">
            <div ref={ticketRef} width="370px">
              <Box
                fontFamily="Montserrat, sans-serif"
                position="relative"
                padding="0"
                maxWidth="370px"
                height="auto"
                border="2px solid"
                borderColor="bgSecondary"
                borderRadius="0"
                backgroundColor="#faf8f4"
              >
                {/* ////////////// */}
                {/* HEADER SECTION */}
                {/* ////////////// */}
                <Box position="relative" height="200px" overflow="hidden" width="100%">
                  <Box
                    width="100%"
                    height="220px"
                    background={`url(${onGetFinalContent('IMG_QR_COVER', IMG_QR_COVER)})`}
                    position="absolute"
                    bgSize="cover"
                    bgPosition="center"
                    zIndex="1"
                  />
                  <Box
                    height="100%"
                    width="100%"
                    backgroundColor="rgba(50, 48, 48, 0.3)"
                    padding="16px"
                    position="absolute"
                    zIndex="2"
                  >
                    <Box pos="absolute" color="#fff" bottom="16px" width="100%" zIndex="3">
                      <Text
                        fontWeight="700"
                        textTransform="uppercase"
                        fontSize="10px"
                        letterSpacing="1px"
                      >
                        {txt.title[lang]}
                      </Text>
                      <Text fontWeight="700" marginTop="12px" fontSize="md">
                        {THE_BRIDE}
                      </Text>
                      <Text marginTop="1px" fontSize="10px" fontWeight="500">
                        {WEDDING_RESEPSI_LOC_NAME}
                      </Text>
                      <Text marginTop="1px" fontSize="10px" fontWeight="500" fontStyle="italic">
                        {WEDDING_RESEPSI_DATE[lang]}
                      </Text>
                    </Box>
                  </Box>
                </Box>

                {/* ///////////////////////// */}
                {/* BADGE VIP OR VVIP SECTION */}
                {/* ///////////////////////// */}
                {isVIP && (
                  <Card
                    width="70px"
                    padding="4px"
                    textTransform="uppercase"
                    backgroundColor="#a1425c"
                    color="#fff"
                    fontWeight="700"
                    borderRadius="16px"
                    textAlign="center"
                    position="absolute"
                    right="16px"
                    marginTop="-16px"
                    shadow="xs"
                    zIndex="4"
                  >
                    <Text>{vip_text}</Text>
                  </Card>
                )}

                {/* //////////// */}
                {/* MAIN SECTION */}
                {/* //////////// */}
                <Box padding="24px 16px">
                  <Flex justify="space-between" gap="16px">
                    <Box width="140px">
                      <Box
                        backgroundColor="#fff"
                        padding="6px"
                        borderRadius="8px"
                        border="2px solid"
                        borderColor="bgSecondary"
                        onClick={onOpen}
                      >
                        <QRCode value={guest.code} size={105} />
                      </Box>
                    </Box>
                    <Box width="100%" textAlign="left">
                      <Text marginTop="8px" fontSize="10px">
                        {txt.dear[lang]}
                      </Text>
                      <Text fontSize="sm" fontWeight="700">
                        {name}
                      </Text>
                      <Text marginTop="8px" fontSize="10px">
                        {txt.information[lang]}
                      </Text>
                      <Text fontSize="xs" fontWeight="700">
                        {desc}
                      </Text>
                      <Text
                        fontSize="xs"
                        fontWeight="700"
                        dangerouslySetInnerHTML={{ __html: validText }}
                      />

                      {ENABLE_SHIFT_TIME_RECEPTION && (
                        <Text fontSize="xs" fontWeight="700">{`Shift ${shift} (${generateShift(
                          shift,
                        )})`}</Text>
                      )}
                    </Box>
                  </Flex>
                  <Text marginTop="16px" textAlign="center" fontSize="10px" fontStyle="italic">
                    {txt.notes[lang]}
                  </Text>
                </Box>

                {/* ////////////// */}
                {/* FOOTER SECTION */}
                {/* ////////////// */}
                <Divider />
                <Box textAlign="center" padding="8px" backgroundColor="bgSecondary">
                  <Center>
                    <Image width="80px" src={LOGO_INVITATO} alt="invitato logo" />
                  </Center>
                </Box>
              </Box>
            </div>
          </Flex>
          {/* ================================ */}
          {/* ========== END QR BOX ========== */}
          {/* ================================ */}
          <Box>
            <Text fontSize="md" textAlign="center" margin="16px 8px" color="mainColorText">
              <span dangerouslySetInnerHTML={{ __html: txt.downloadDesc[lang] }} />
            </Text>
            <Center>
              {isDirectLink ? (
                <QRButton
                  text={txt.btnDownload[lang]}
                  bgColor="bgAlternative"
                  color="mainColorTextLight"
                  size="sm"
                  fontSize="md"
                  fontWeight="bold"
                  fontStyle="italic"
                  padding="6px 8px"
                />
              ) : (
                <LazyExportPNG componentRef={ticketRef} autoDownload={isAutoDownload} />
              )}
            </Center>
            {!isDirectLink && (
              <Center>
                <Button
                  marginTop="16px"
                  leftIcon={<BiArrowBack />}
                  onClick={() => navigate(-1)}
                  bgColor="bgSecondary"
                  color="secondaryColorText"
                  fontSize="md"
                  padding="6px 24px"
                >
                  {txt.back[lang]}
                </Button>
              </Center>
            )}
          </Box>
        </>
      )}
      {/* ===== MODAL QR ZOOM ===== */}
      <>
        <Modal isOpen={isOpen} onClose={onClose} isCentered>
          <ModalOverlay
            bg="rgba(0, 0, 0, 0.6)" // Dark semi-transparent background
            backdropFilter="blur(10px)" // Blurry effect
          />
          <ModalContent backgroundColor="transparent" boxShadow="none">
            <ModalCloseButton right="35px" top="-20px" color="#fff" />
            <ModalBody>
              <Center>
                <Box
                  backgroundColor="#fff"
                  padding="6px"
                  borderRadius="8px"
                  border="2px solid"
                  borderColor="bgSecondary"
                >
                  <QRCode value={guest.code} maxSize={200} />
                </Box>
              </Center>
            </ModalBody>
          </ModalContent>
        </Modal>
      </>
    </Box>
  );
}

QRCardSection.propTypes = {
  isDirectLink: bool,
};

QRCardSection.defaultProps = {
  isDirectLink: true,
};

export default React.memo(QRCardSection);
